import React from "react";
import { useParams } from "react-router-dom";
import { ArrowRight, Download } from "lucide-react";
import { BACKEND_URL } from "../lib/api";
import {
  RENA, useRenalifePage, useRenalifeCta, Eyebrow, PageShell, LoadingScreen, NotFoundScreen, VideoBlock, Footer,
} from "./RenalifeLanding";

const Node = ({ label, sub, root }) => (
  <div className="flex flex-col items-center">
    <div
      className={`rounded-full flex items-center justify-center font-bold ${root ? "w-20 h-20 text-lg" : "w-14 h-14 text-sm"}`}
      style={root ? { background: RENA.gold, color: RENA.deep } : { background: "#064E3B", color: "#F8FAF8", border: `1px solid ${RENA.gold}50` }}
    >
      {label}
    </div>
    {sub && <div className="font-mono text-[10px] mt-1.5" style={{ color: RENA.gold }}>{sub}</div>}
  </div>
);

export default function RenalifeMatching() {
  const { slug } = useParams();
  const { data, notFound } = useRenalifePage(slug);
  const { go, sending } = useRenalifeCta(slug, "bono2");

  if (notFound) return <NotFoundScreen />;
  if (!data) return <LoadingScreen />;

  const img = data.assets?.bono2_matching?.image_url;
  const video = data.assets?.bono2_matching?.video_url;

  return (
    <PageShell testid="renalife-landing-bono2">
      {/* 1) HERO */}
      <section className="min-h-screen flex items-center px-6 sm:px-12" style={{ background: `linear-gradient(160deg, ${RENA.deep} 0%, ${RENA.forest} 120%)` }}>
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 items-center py-16 w-full">
          <div>
            <Eyebrow>BONO 002 · MULTIPLICADOR INSTANTÁNEO</Eyebrow>
            <h1 className="font-renalife font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.08] mt-5">
              Cuando tu red crece, tú cobras <em className="italic font-medium" style={{ color: RENA.gold }}>dos veces</em>.
            </h1>
            <p className="text-base sm:text-lg mt-6 max-w-xl" style={{ color: "#cbd5d1" }}>
              Cada vez que uno de tus directos (N1) registra a un nuevo distribuidor, tú recibes $500 MXN adicionales —
              al mismo tiempo que ellos reciben su Bono Inicio Rápido.
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mt-8 max-w-xl">
              <div data-testid="compare-n1" className="rounded-2xl border p-5" style={{ borderColor: "#ffffff20", background: "#03251c" }}>
                <div className="font-mono text-[11px] uppercase tracking-widest" style={{ color: "#94a3b8" }}>Solo N1 (Inicio Rápido)</div>
                <div className="font-renalife text-2xl mt-2">$500 MXN <span className="text-sm" style={{ color: "#94a3b8" }}>por directo</span></div>
              </div>
              <div data-testid="compare-n2" className="rounded-2xl border p-5" style={{ borderColor: `${RENA.gold}60`, background: "#04382b" }}>
                <div className="font-mono text-[11px] uppercase tracking-widest" style={{ color: RENA.gold }}>N1 + N2 (con Matching)</div>
                <div className="font-renalife text-2xl mt-2" style={{ color: RENA.gold }}>$1,000 MXN <span className="text-sm text-[#cbd5d1]">combinados en tu línea</span></div>
              </div>
            </div>
            <button
              data-testid="cta-teach"
              onClick={go}
              disabled={sending}
              className="mt-9 rounded-full px-8 py-4 font-bold text-base transition-transform hover:-translate-y-0.5"
              style={{ background: RENA.gold, color: RENA.deep }}
            >
              {sending ? "Abriendo WhatsApp…" : "Enséñale a tu red a crecer"}
            </button>
          </div>
          {img && (
            <img data-testid="hero-image-bono2" src={`${BACKEND_URL}${img}`} alt="Matching Bonus" className="rounded-3xl border shadow-2xl w-full object-cover" style={{ borderColor: `${RENA.gold}30` }} />
          )}
        </div>
      </section>

      {/* 2) LA MAGIA DEL DUPLICADO */}
      <section className="px-6 sm:px-12 py-20" style={{ background: "#03251c" }}>
        <div className="max-w-4xl mx-auto text-center">
          <Eyebrow>La magia del duplicado</Eyebrow>
          <h2 className="font-renalife text-2xl sm:text-3xl mt-4">Cada registro ilumina dos niveles a la vez.</h2>
          <div data-testid="tree-diagram" className="mt-12">
            <div className="flex justify-center"><Node label="TÚ" sub="+$500 Matching" root /></div>
            <div className="w-px h-8 mx-auto" style={{ background: `${RENA.gold}60` }} />
            <div className="grid grid-cols-3 gap-4 max-w-lg mx-auto">
              {["Ana", "Luis", "Marco"].map((n) => (
                <div key={n} className="flex flex-col items-center">
                  <Node label={n} sub="+$500 Inicio" />
                  <div className="w-px h-6" style={{ background: `${RENA.gold}40` }} />
                  <div className="flex gap-1.5">
                    {[1, 2, 3].map((i) => (
                      <span key={i} className="w-3.5 h-3.5 rounded-full animate-pulse" style={{ background: RENA.gold }} />
                    ))}
                  </div>
                  <div className="font-mono text-[10px] mt-1.5" style={{ color: "#94a3b8" }}>3 nuevos N2</div>
                </div>
              ))}
            </div>
          </div>
          <div data-testid="tree-total" className="font-renalife text-3xl sm:text-5xl mt-12">
            <span style={{ color: RENA.gold }}>$4,500 MXN</span> solo de matching
          </div>
          <p className="text-sm mt-3 max-w-md mx-auto" style={{ color: "#a7bab3" }}>
            si tus 3 directos registran 3 personas cada uno este mes.
          </p>
          <p className="text-xs mt-4" style={{ color: "#64748b" }}>Ejemplo de cálculo, no una promesa de ingresos. Tus resultados dependen de tu esfuerzo.</p>
        </div>
      </section>

      {/* 3) ¿POR QUÉ EXISTE ESTE BONO? */}
      <section className="max-w-3xl mx-auto px-6 sm:px-12 py-20">
        <Eyebrow>¿Por qué existe este bono?</Eyebrow>
        <div data-testid="philosophy-text" className="mt-6 space-y-5 text-base sm:text-lg leading-relaxed" style={{ color: "#cbd5d1" }}>
          <p>
            En la mayoría de los sistemas de multinivel, patrocinar a alguien es el final de la historia: firmas,
            cobras y sigues buscando al siguiente. Renalife México decidió que ese modelo estaba incompleto.
            El Matching Bonus existe para recompensar a los distribuidores que se toman el tiempo de <b style={{ color: RENA.off }}>entrenar</b> a
            sus directos, no solo de registrarlos y olvidarse.
          </p>
          <p>
            Cuando tú le enseñas a tu N1 cómo compartir su link, cómo presentar el kit y cómo acompañar a su primera
            persona, esa enseñanza produce registros que tú no hiciste con tus manos — pero que existen gracias a ti.
            El sistema lo reconoce en el mismo instante: ellos cobran su Bono Inicio Rápido y tú cobras tu Matching de
            $500 MXN, el mismo día, sin corte mensual de por medio.
          </p>
          <p>
            El resultado es un incentivo raro en esta industria: mientras mejor formas a tu gente, mejor te va a ti.
            El mentorazgo deja de ser un acto de buena voluntad y se convierte en un activo financiero real, medible
            en pesos y pagado al momento.
          </p>
          <p className="font-renalife text-xl sm:text-2xl" style={{ color: RENA.off }}>
            No es un bono de suerte. Es un bono de coherencia.
          </p>
        </div>
      </section>

      <VideoBlock videoUrl={video} title="El Matching Bonus en 25 segundos" />

      {/* 4) TESTIMONIO + CTA */}
      <section className="max-w-4xl mx-auto px-6 sm:px-12 py-20 text-center">
        <blockquote className="font-renalife text-2xl sm:text-4xl leading-snug">
          “Cuando entendí que mi red también me pagaba por ENSEÑAR a los míos, dejé de perseguir prospectos y empecé a formar líderes.”
        </blockquote>
        <div className="font-mono text-sm mt-6" style={{ color: "#94a3b8" }}>— Roberto D., 41, Monterrey.</div>
      </section>
      <button
        data-testid="cta-guide"
        onClick={go}
        disabled={sending}
        className="w-full py-8 font-renalife font-bold text-xl sm:text-3xl transition-opacity hover:opacity-90 flex items-center justify-center gap-3"
        style={{ background: RENA.gold, color: RENA.deep }}
      >
        <Download size={26} /> Descarga la guía de mentorazgo Renalife <ArrowRight size={26} />
      </button>
      <Footer name={data.distributor_name} />
    </PageShell>
  );
}
